import Link from 'next/link';
import Button from '@monofront/design-system/components/Button';
import { useAuthentication } from '@context/businessContainer';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import { useTranslations } from 'next-intl';
import ABTestEnabled from '@components/ABTestEnabled/ABTestEnabled';
import { ABTEST_NEW_404_PAGE } from '@monofront/business/constants';
import { LayoutContext } from '@context/LayoutContext';
import { useContext } from 'react';
import { useMediaQuery } from '@monofront/business/hooks';
import { SlicesContainer } from '@screens/SlicesContainer';

type PropTypesA = {
  statusCode: number;
};

const CustomErrorCaseA: React.FC<PropTypesA> = ({ statusCode }) => {
  const { isAuthenticated } = useAuthentication();
  const tErrors = useTranslations('Errors');

  return (
    <>
      <div className="container">
        <div className="content">
          <div className="img">
            <ResponsiveImage
              src="/images/error.png"
              alt=""
              width={110}
              height={146}
            />
          </div>
          <h1>
            {tErrors.rich('error', {
              code: statusCode,
            })}
          </h1>
          <div className="card-content card-header">
            <p>{tErrors('aConsolationRiddle')}</p>
          </div>
          <div className="card-content">
            {tErrors.rich('aConsolationRiddleAnswer', {
              question: (children) => <p className="question">{children}</p>,
              answer: (children) => <p className="answer">{children}</p>,
            })}
          </div>
          <Button>
            <Link href={isAuthenticated ? '/home' : '/'} legacyBehavior>
              <a>{tErrors('backToSite')}</a>
            </Link>
          </Button>
        </div>
      </div>
      <style jsx>{`
        .content {
          margin: 0 auto;
          width: 450px;
          max-width: 100%;
          padding: 35px 0;
        }

        .img {
          display: flex;
          justify-content: center;
          margin-bottom: 48px;
        }

        .content > h1 {
          text-align: center;
          margin-bottom: 32px;
        }

        .content > :global(button) {
          margin-top: 32px;
        }

        .card-content {
          padding: 24px 12px;
          text-align: center;
        }

        .question {
          margin-bottom: var(--space-4);
          font-size: var(--font-size-3);
        }

        .card-header > p {
          font-size: var(--font-size-3);
        }

        @media (max-width: 800px) {
          .content {
            padding: var(--padding-layout-sm);
          }
        }
      `}</style>
    </>
  );
};

const CustomErrorCaseB: React.FC = () => {
  const { errorTitle, errorText, errorImage, errorSlices } =
    useContext(LayoutContext);
  const isMobile = useMediaQuery(800);

  const tGlobal = useTranslations('Global');

  if (!errorTitle || !errorText || !errorImage || !errorSlices) {
    return CustomErrorCaseA({ statusCode: 404 });
  }

  return (
    <div className="tw-py-8">
      <div className="tw-flex tw-flex-col tw-items-center tw-px-4">
        <div
          className="tw-relative tw-mb-6"
          style={{
            height: isMobile ? 127 : 224,
            width: isMobile ? 114 : 201,
          }}
        >
          <ResponsiveImage
            src={errorImage.url}
            alt={errorImage.alt}
            layout="fill"
          />
        </div>
        <h1 className="tw-lf-title-md tw-mb-2 tw-text-center desktop:tw-lf-title-lg">
          {errorTitle}
        </h1>
        <p className="tw-lf-body-regular-md tw-text-center">{errorText}</p>
      </div>
      <SlicesContainer
        pageContents={errorSlices}
        UNSAFE_className="
        desktop:!tw-mt-12
        !tw-mt-8
        [&>div]:desktop:!tw-mb-12
        [&>div]:!tw-mb-8
        [&_h1]:!tw-lf-title-sm
        [&_h1]:desktop:tw-lf-title-md"
      />

      <div className="tw-flex tw-flex-col tw-items-center tw-px-4">
        <Link href="/home" legacyBehavior>
          <a className="tw-lf-button tw-lf-button-primary tw-lf-button-md tw-px-7">
            {tGlobal('backToHomePage')}
          </a>
        </Link>
      </div>
    </div>
  );
};

const CustomError: React.FC<PropTypesA> = ({ statusCode }) => (
  <ABTestEnabled.Provider feature={ABTEST_NEW_404_PAGE}>
    <ABTestEnabled.A>
      <CustomErrorCaseA statusCode={statusCode} />
    </ABTestEnabled.A>
    <ABTestEnabled.B>
      <CustomErrorCaseB />
    </ABTestEnabled.B>
  </ABTestEnabled.Provider>
);

export default CustomError;
